import { SIMAElementClassifier, } from "../types";
import { SurveyPointIndex } from "./survey-point-index";
/**
 * 三斜法・求積計算ブロック内の単一三角形 (E01 頂点 + E03 寸法)。
 * 頂点 A-B が底辺、頂点 C が頂高 (apex) を形成します。
 */
export class Triangle {
    vertex;
    dimension;
    ptA_point;
    ptB_point;
    ptC_point;
    constructor(data) {
        this.vertex = data?.vertex ?? {
            classifier: SIMAElementClassifier.TRIANGULATION_VERTEX,
            ptA_number: 1,
            ptA_name: "A",
            ptB_number: 2,
            ptB_name: "B",
            ptC_number: 3,
            ptC_name: "C",
        };
        this.dimension = data?.dimension;
    }
    get triangleNumber() {
        return this.vertex.triangle_number;
    }
    get base() {
        if (this.dimension?.base !== undefined && this.dimension.base > 0) {
            return this.dimension.base;
        }
        if (this.dimension?.side_ab !== undefined && this.dimension.side_ab > 0) {
            return this.dimension.side_ab;
        }
        if (this.ptA_point && this.ptB_point) {
            const dx = this.ptB_point.x - this.ptA_point.x;
            const dy = this.ptB_point.y - this.ptA_point.y;
            return Math.hypot(dx, dy);
        }
        return 0;
    }
    get height() {
        if (this.dimension?.height !== undefined && this.dimension.height > 0) {
            return this.dimension.height;
        }
        const b = this.base;
        if (b > 0) {
            return (2.0 * this.area) / b;
        }
        return 0;
    }
    get area() {
        if (this.dimension?.area !== undefined && this.dimension.area > 0) {
            return this.dimension.area;
        }
        if (this.dimension?.base !== undefined &&
            this.dimension?.height !== undefined &&
            this.dimension.base > 0 &&
            this.dimension.height > 0) {
            return (this.dimension.base * this.dimension.height) / 2.0;
        }
        if (this.ptA_point && this.ptB_point && this.ptC_point) {
            const cross = (this.ptB_point.x - this.ptA_point.x) *
                (this.ptC_point.y - this.ptA_point.y) -
                (this.ptC_point.x - this.ptA_point.x) *
                    (this.ptB_point.y - this.ptA_point.y);
            return Math.abs(cross) / 2.0;
        }
        return 0;
    }
    /**
     * 注入された SurveyPointIndex または SurveyPoint 配列を用いて頂点 A, B, C の測量座標を解決します。
     * 三角形ごとの Map 再構築を行わず O(1) で解決します。
     */
    resolvePoints(indexOrPoints) {
        const index = Array.isArray(indexOrPoints)
            ? new SurveyPointIndex(indexOrPoints)
            : indexOrPoints;
        this.ptA_point = index.lookup(this.vertex.ptA_number, this.vertex.ptA_name);
        this.ptB_point = index.lookup(this.vertex.ptB_number, this.vertex.ptB_name);
        this.ptC_point = index.lookup(this.vertex.ptC_number, this.vertex.ptC_name);
    }
    toTriangleData() {
        return {
            vertex: { ...this.vertex },
            dimension: this.dimension ? { ...this.dimension } : undefined,
        };
    }
}
/**
 * 三斜法面積計算ブロック (E00..E99 ブロック)。
 */
export class TriangulationBlock {
    begin;
    triangles = [];
    _surveyPointIndex;
    constructor(data, surveyPointsOrIndex) {
        const input = data;
        const begin = input?.begin;
        this.begin = begin ?? {
            classifier: SIMAElementClassifier.TRIANGULATION_BEGIN,
            block_number: input?.blockNumber ?? 1,
            block_name: input?.blockName ?? "",
            lot_number: input?.lotNumber,
        };
        if (input?.triangles) {
            this.triangles = input.triangles.map((t) => t instanceof Triangle ? t : new Triangle(t));
        }
        if (surveyPointsOrIndex) {
            this.resolvePoints(surveyPointsOrIndex);
        }
    }
    get blockNumber() {
        return this.begin.block_number;
    }
    set blockNumber(val) {
        this.begin.block_number = val;
    }
    get blockName() {
        return this.begin.block_name;
    }
    set blockName(val) {
        this.begin.block_name = val;
    }
    get lotNumber() {
        return this.begin.lot_number;
    }
    set lotNumber(val) {
        this.begin.lot_number = val;
    }
    get totalArea() {
        return this.triangles.reduce((sum, tri) => sum + tri.area, 0);
    }
    addTriangle(triangle) {
        const instance = triangle instanceof Triangle ? triangle : new Triangle(triangle);
        if (this._surveyPointIndex) {
            instance.resolvePoints(this._surveyPointIndex);
        }
        this.triangles.push(instance);
        return instance;
    }
    /**
     * SurveyPointIndex または SurveyPoint 配列を用いてブロック内の全三角形の頂点座標を一括解決します。
     */
    resolvePoints(indexOrPoints) {
        const index = Array.isArray(indexOrPoints)
            ? new SurveyPointIndex(indexOrPoints)
            : indexOrPoints;
        this._surveyPointIndex = index;
        for (const tri of this.triangles) {
            tri.resolvePoints(index);
        }
    }
    toTriangulationData() {
        return {
            begin: { ...this.begin },
            triangles: this.triangles.map((t) => t.toTriangleData()),
        };
    }
}
//# sourceMappingURL=triangulation.js.map